import { format, parse } from "date-fns";
import React, { useEffect, useState } from "react";
import {
  getDashBoardMain1M1,
  getDashBoardMain2M1,
  getDashBoardMain3M1,
} from "../../Service/API";
import {
  SubTitle,
  SumTd,
  Table,
  TableContainer,
  Td,
  Th,
  Tr,
} from "./HomeStyle";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  LabelList,
} from "recharts";

const FarmHome = (props) => {
  const [data1, setData1] = useState([]); // 주간 사육동향 (육성우)
  const [data2, setData2] = useState(null); // 입식구분별 (육성우)
  const [data3, setData3] = useState(null); // 우사별 (육성우)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRow, setTotalRow] = useState(null); // 합계 데이터 관리

  const [curDate, setCurDate] = useState(new Date());

  const [chartData, setChartData] = useState([
    {
      TRUST_CNT_TOT: 0,
      COOP_CNT_TOT: 0,
    },
  ]);

  // 초기 실행
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        await fetchData();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  // API 호출 함수
  const fetchData = async (cuCode) => {
    try {
      setLoading(true);
      const [result1, result2, result3] = await Promise.all([
        getDashBoardMain1M1(),
        getDashBoardMain2M1(),
        getDashBoardMain3M1(),
      ]);

      setData1(result1 || []);
      setData2(result2 || {});
      setData3(result3 || []);

      const totalRow = await calculateTotal(result3);
      setTotalRow(totalRow);

      const tempChartdata = convertToChartData(result2);
      setChartData(tempChartdata);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (data) => {
    // 초기 합계 값 설정
    const total = data.reduce(
      (acc, item) => {
        acc.M1_01_CNT += item.M1_01_CNT || 0;
        acc.M1_02_CNT += item.M1_02_CNT || 0;
        acc.M1_03_CNT += item.M1_03_CNT || 0;
        acc.M1_ETC_CNT += item.M1_ETC_CNT || 0;
        acc.CNT_TOT += item.CNT_TOT || 0;
        return acc;
      },
      { M1_01_CNT: 0, M1_02_CNT: 0, M1_03_CNT: 0, M1_ETC_CNT: 0, CNT_TOT: 0 }
    );

    return {
      ...total, // 계산된 합계 값 포함
    };
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        >
          {payload.map((entry, index) => (
            <p
              key={`tooltip-${index}`}
              style={{ color: entry.color, margin: 0 }}
            >
              {`${entry.dataKey} : ${entry.value.toLocaleString()}%`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatValue = (value) => (value ? value.toLocaleString() : "");

  const calculateRatio = (value, total) => ((value / total) * 100).toFixed(1);

  const convertToChartData = (data) => {
    if (!data) return [];

    return [
      {
        name: "총 두수",
        TRUST_CNT_TOT: calculateRatio(data.TRUST_CNT_TOT || 0, data.CNT_TOT),
        COOP_CNT_TOT: calculateRatio(data.COOP_CNT_TOT || 0, data.CNT_TOT),
      },
    ];
  };

  const maxValue = 100;

  return (
    <>
      <SubTitle width="120px">주간 사육동향</SubTitle>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          width={600}
          height={300}
          data={data1}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="YMD"
            tickFormatter={(value) => {
              if (value.length === 8) {
                const month = value.substring(4, 6); // 월 추출
                const day = value.substring(6, 8); // 일 추출
                return `${month}-${day}`; // "월-일" 형식 반환
              }
              return value; // 기본값 반환
            }}
            tick={{
              angle: -45,
              fontSize: 12,
              fill: "black",
            }}
            height={60}
            interval={0}
            tickMargin={22}
          />
          <YAxis />
          <Tooltip
            labelFormatter={(value) => {
              if (value.length === 8) {
                const year = value.substring(0, 4);
                const month = value.substring(4, 6);
                const day = value.substring(6, 8);
                return `${year}-${month}-${day}`;
              }
              return value;
            }}
            formatter={(value, name) => {
              const formattedValue = value.toLocaleString();
              if (name === "M1_01_CNT") return [formattedValue, "후보우"];
              if (name === "M1_02_CNT") return [formattedValue, "수정우"];
              if (name === "M1_03_CNT") return [formattedValue, "임신우"];
              return [formattedValue, name];
            }}
          />
          <Legend
            formatter={(value) => {
              if (value === "M1_01_CNT") return "후보우";
              if (value === "M1_02_CNT") return "수정우";
              if (value === "M1_03_CNT") return "임신우";
              return value;
            }}
          />
          <Bar
            dataKey="M1_01_CNT"
            stackId="a"
            fill="#0088FE"
            label={{
              position: "inside",
              fill: "white",
              fontSize: 14,
              formatter: (value) => value.toLocaleString(), // 쉼표 추가
            }}
          />
          <Bar
            dataKey="M1_02_CNT"
            stackId="a"
            fill="#00C49F"
            label={{
              position: "inside",
              fill: "white",
              fontSize: 14,
              formatter: (value) => value.toLocaleString(), // 쉼표 추가
            }}
          />
          <Bar
            dataKey="M1_03_CNT"
            stackId="a"
            fill="#FFBB28"
            label={{
              position: "inside",
              fill: "black",
              fontSize: 14,
              formatter: (value) => value.toLocaleString(), // 쉼표 추가
            }}
          />
        </BarChart>
      </ResponsiveContainer>
      <TableContainer>
        <Table>
          <Tr>
            <Th rowSpan={2}>일자</Th>
            <Th colSpan={5}>사육두수</Th>
          </Tr>
          <Tr>
            <Th>후보우</Th>
            <Th>수정우</Th>
            <Th>임신우</Th>
            <Th>기타</Th>
            <Th>계</Th>
          </Tr>
          {data1?.map((item, index) => (
            <Tr>
              <Td textAlign="center">
                {item.YMD && item.YMD.length === 8
                  ? format(parse(item.YMD, "yyyyMMdd", new Date()), "yy-MM-dd")
                  : item.YMD}
              </Td>
              <Td>{formatValue(item.M1_01_CNT)}</Td>
              <Td>{formatValue(item.M1_02_CNT)}</Td>
              <Td>{formatValue(item.M1_03_CNT)}</Td>
              <Td>{formatValue(item.M1_ETC_CNT)}</Td>
              <Td>{formatValue(item.CNT_TOT)}</Td>
            </Tr>
          ))}
        </Table>
      </TableContainer>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          margin: "5px 0px 5px 0px",
        }}
      >
        ({curDate.toLocaleDateString()} 현재)
      </div>

      <TableContainer>
        <Table>
          <Tr>
            <Th>구분</Th>
            <Th>후보우</Th>
            <Th>수정우</Th>
            <Th>임신우</Th>
            <Th>기타</Th>
            <Th>계</Th>
          </Tr>
          <Tr>
            <Td textAlign="center">위탁우</Td>
            <Td>{data2 ? formatValue(data2.TRUST_M1_01_CNT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.TRUST_M1_02_CNT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.TRUST_M1_03_CNT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.TRUST_M1_ETC_CNT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.TRUST_CNT_TOT) : ""}</Td>
          </Tr>
          <Tr>
            <Td textAlign="center">매취우</Td>
            <Td>{data2 ? formatValue(data2.COOP_M1_01_CNT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.COOP_M1_02_CNT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.COOP_M1_03_CNT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.COOP_M1_ETC_CNT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.COOP_CNT_TOT) : ""}</Td>
          </Tr>
          <Tr>
            <SumTd textAlign="center">계</SumTd>
            <SumTd>{data2 ? formatValue(data2.M1_01_CNT) : ""}</SumTd>
            <SumTd>{data2 ? formatValue(data2.M1_02_CNT) : ""}</SumTd>
            <SumTd>{data2 ? formatValue(data2.M1_02_CNT) : ""}</SumTd>
            <SumTd>{data2 ? formatValue(data2.M1_ETC_CNT) : ""}</SumTd>
            <SumTd>{data2 ? formatValue(data2.CNT_TOT) : ""}</SumTd>
          </Tr>
          <Tr>
            <Td textAlign="center">비율(%)</Td>
            <Td>{data2 ? formatValue(data2.M1_01_RAT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.M1_02_RAT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.M1_03_RAT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.M1_ETC_RAT) : ""}</Td>
            <Td>{data2 ? formatValue(data2.CNT_TOT_RAT) : ""}</Td>
          </Tr>
        </Table>
      </TableContainer>

      <div style={{ margin: "10px 0px 10px 0px" }}>
        <ResponsiveContainer width="100%" height={100}>
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              domain={[0, 100]}
              ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis type="category" dataKey="name" hide={true} />
            <Tooltip
              formatter={(value, name) => {
                if (name === "TRUST_CNT_TOT") return [`${value}%`, "위탁우"];
                if (name === "COOP_CNT_TOT") return [`${value}%`, "매취우"];
                return [value, name];
              }}
            />
            <Legend
              formatter={(value) => {
                if (value === "TRUST_CNT_TOT") return "위탁우";
                if (value === "COOP_CNT_TOT") return "매취우";
                return value;
              }}
            />
            <Bar
              dataKey="TRUST_CNT_TOT"
              stackId="a"
              fill={"#0088FE"}
              label={{
                position: "inside",
                fill: "white",
                fontSize: 14,
                formatter: (value) => value.toLocaleString(), // 쉼표 추가
              }}
            />
            <Bar
              dataKey="COOP_CNT_TOT"
              stackId="a"
              fill={"#FF8042"}
              // label={{
              //   position: "inside",
              //   fill: "white",
              //   fontSize: 14,
              //   formatter: (value) => value.toLocaleString(), // 쉼표 추가
              // }}
            >
              <LabelList
                dataKey="COOP_CNT_TOT"
                position="inside"
                fontSize="14"
                fill="white"
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <SubTitle>우사별 사육현황</SubTitle>
      <TableContainer>
        <Table>
          <Tr>
            <Th>우사</Th>
            <Th>후보우</Th>
            <Th>수정우</Th>
            <Th>임신우</Th>
            <Th>기타</Th>
            <Th>계</Th>
          </Tr>
          {data3?.map((item, index) => (
            <Tr>
              <Td textAlign="center">{item.HOUSE_NM}</Td>
              <Td>{formatValue(item.M1_01_CNT)}</Td>
              <Td>{formatValue(item.M1_02_CNT)}</Td>
              <Td>{formatValue(item.M1_03_CNT)}</Td>
              <Td>{formatValue(item.M1_ETC_CNT)}</Td>
              <Td>{formatValue(item.CNT_TOT)}</Td>
            </Tr>
          ))}
          {totalRow && (
            <Tr>
              <SumTd textAlign="center">합계</SumTd>
              <SumTd>{formatValue(totalRow.M1_01_CNT)}</SumTd>
              <SumTd>{formatValue(totalRow.M1_02_CNT)}</SumTd>
              <SumTd>{formatValue(totalRow.M1_03_CNT)}</SumTd>
              <SumTd>{formatValue(totalRow.M1_ETC_CNT)}</SumTd>
              <SumTd>{formatValue(totalRow.CNT_TOT)}</SumTd>
            </Tr>
          )}
        </Table>
      </TableContainer>
    </>
  );
};

export default FarmHome;
